/**
 * Shared Logger utility for all LDD projects
 * 
 * Usage:
 * 
 * 1. Import in your files:
 *    import { Logger } from '../../../share/utils/logger';
 * 
 * 2. Use the logger:
 *    Logger.debug('Debug message', { data: 'optional' });
 *    Logger.info('Info message');
 *    Logger.warn('Warning message');
 *    Logger.error('Error occurred', error);
 * 
 * 3. Configure log level at app startup (optional):
 *    Logger.setLevel(process.env.NODE_ENV === 'production' ? LOG_LEVELS.INFO : LOG_LEVELS.DEBUG);
 * 
 * Default log level: INFO (shows INFO, WARN, ERROR)
 */

export const LOG_LEVELS = {
    DEBUG: 'debug',
    INFO: 'info',
    WARN: 'warn',
    ERROR: 'error'
  } as const;
  
  export type LogLevel = typeof LOG_LEVELS[keyof typeof LOG_LEVELS];
  
  export class Logger {
    // Default to INFO level - each project can configure this at startup
    private static currentLevel: LogLevel = LOG_LEVELS.INFO;
    
    private static shouldLog(level: LogLevel): boolean {
      const levels = [LOG_LEVELS.DEBUG, LOG_LEVELS.INFO, LOG_LEVELS.WARN, LOG_LEVELS.ERROR];
      const currentIndex = levels.indexOf(this.currentLevel);
      const messageIndex = levels.indexOf(level);
      return messageIndex >= currentIndex;
    }

    /**
     * Recursively serialize Error objects in data structures
     * Error objects don't serialize well with JSON.stringify() because their
     * properties (message, stack, name) are non-enumerable
     */
    private static serializeErrorObject(data: any): any {
      if (data instanceof Error) {
        return {
          message: data.message,
          name: data.name,
          stack: data.stack
        };
      }
      
      if (Array.isArray(data)) {
        return data.map(item => this.serializeErrorObject(item));
      }
      
      if (data && typeof data === 'object') {
        const serialized: Record<string, any> = {};
        for (const key in data) {
          if (Object.prototype.hasOwnProperty.call(data, key)) {
            serialized[key] = this.serializeErrorObject(data[key]);
          }
        }
        return serialized;
      }
      
      return data;
    }
    
    private static formatMessage(level: LogLevel, message: string, data?: any): string {
      const timestamp = new Date().toISOString();
      const levelStr = level.toUpperCase().padEnd(5);
      const baseMessage = `[${timestamp}] ${levelStr} ${message}`;
      
      if (data) {
        const serializedData = this.serializeErrorObject(data);
        return `${baseMessage} ${JSON.stringify(serializedData, null, 2)}`;
      }
      
      return baseMessage;
    }
    
    static debug(message: string, data?: any): void {
      if (this.shouldLog(LOG_LEVELS.DEBUG)) {
        console.log(this.formatMessage(LOG_LEVELS.DEBUG, message, data));
      }
    }
    
    static info(message: string, data?: any): void {
      if (this.shouldLog(LOG_LEVELS.INFO)) {
        console.log(this.formatMessage(LOG_LEVELS.INFO, message, data));
      }
    }
    
    static warn(message: string, data?: any): void {
      if (this.shouldLog(LOG_LEVELS.WARN)) {
        console.warn(this.formatMessage(LOG_LEVELS.WARN, message, data));
      }
    }
    
    static error(message: string, error?: any): void {
      if (this.shouldLog(LOG_LEVELS.ERROR)) {
        console.error(this.formatMessage(LOG_LEVELS.ERROR, message, error));
      }
    }
    
    // Set log level at runtime (call this in your app startup to configure)
    static setLevel(level: LogLevel): void {
      this.currentLevel = level;
    }
  }

